import { BrandingConfig, defaultBranding } from '../types/branding';

/**
 * Normalizes branding configuration by merging stored values with defaults
 * to guarantee safe values for all required properties.
 */
export function normalizeBranding(raw: any): BrandingConfig {
  const b = (raw || {}) as BrandingConfig;
  
  return {
    primaryColor: b.primaryColor || defaultBranding.primaryColor,
    secondaryColor: b.secondaryColor || defaultBranding.secondaryColor,
    accentColor: b.accentColor || defaultBranding.accentColor,
    textOnPrimary: b.textOnPrimary || defaultBranding.textOnPrimary,
    buttonRadiusPx: typeof b.buttonRadiusPx === 'number' ? b.buttonRadiusPx : defaultBranding.buttonRadiusPx,
    showAdminLoginLink: typeof b.showAdminLoginLink === 'boolean' ? b.showAdminLoginLink : defaultBranding.showAdminLoginLink,
    isDarkHeader: typeof b.isDarkHeader === 'boolean' ? b.isDarkHeader : defaultBranding.isDarkHeader,
    logoUrl: b.logoUrl || '',
    faviconUrl: b.faviconUrl || '',
    ogImageUrl: b.ogImageUrl || '',
    customCss: b.customCss || ''
  };
}

/**
 * Validates a hex color string
 */
export function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

/**
 * Validates a URL string
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Clamps a number between min and max values
 */
export function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/**
 * Calculates contrast ratio between two hex colors
 * Returns a value between 1 and 21, where higher values indicate better contrast
 */
export function calculateContrastRatio(color1: string, color2: string): number {
  const getLuminance = (hex: string): number => {
    const rgb = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (!rgb) return 0;
    
    const [r, g, b] = [
      parseInt(rgb[1], 16) / 255,
      parseInt(rgb[2], 16) / 255,
      parseInt(rgb[3], 16) / 255
    ].map(c => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  
  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Checks if contrast ratio meets WCAG AA standards (4.5:1)
 */
export function hasGoodContrast(color1: string, color2: string): boolean {
  return calculateContrastRatio(color1, color2) >= 4.5;
}










